import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { 
  ArrowLeft, Package, Tag, MapPin, 
  Info, ShoppingBag, FileText, AlertCircle,
  Camera, Upload, X, Trash2, Image as ImageIcon, Loader2
} from 'lucide-react';

const conditionOptions = [
  { value: 'excellent', label: 'Eccellente', emoji: '✨' },
  { value: 'good', label: 'Buono', emoji: '👍' },
  { value: 'fair', label: 'Discreto', emoji: '👌' },
  { value: 'poor', label: 'Scarso', emoji: '⚠️' },
  { value: 'broken', label: 'Rotto', emoji: '❌' }
];

export default function EditAsset() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [condition, setCondition] = useState('good');
  const [serialNumber, setSerialNumber] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [notes, setNotes] = useState('');
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [originalPhotoUrls, setOriginalPhotoUrls] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Fetch asset data
  const { data, isLoading, error } = useQuery({
    queryKey: ['asset', id],
    queryFn: () => api.getAsset(id!),
    refetchOnMount: 'always'
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: api.getCategories,
    refetchOnMount: 'always'
  });

  // Populate form when data loads
  useEffect(() => {
    if (data?.asset) {
      setName(data.asset.name || '');
      setDescription(data.asset.description || '');
      setCategoryId(data.asset.category?.id || '');
      setCondition(data.asset.condition || 'good');
      setSerialNumber(data.asset.serialNumber || '');
      setBrand(data.asset.brand || '');
      setModel(data.asset.model || '');
      setNotes(data.asset.notes || '');
      // Parse photoUrls from JSON string
      try {
        const urls = data.asset.photoUrls ? JSON.parse(data.asset.photoUrls) : [];
        const urlsArray = Array.isArray(urls) ? urls : [];
        setPhotoUrls(urlsArray);
        setOriginalPhotoUrls(urlsArray);
      } catch {
        setPhotoUrls([]);
        setOriginalPhotoUrls([]);
      }
    }
  }, [data]);

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (updateData: any) => api.updateAsset(id!, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      navigate(`/assets/${id}`);
    }
  });

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadError(null);
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('entityType', 'asset');
        formData.append('entityId', id!);

        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json() as { error?: string };
          throw new Error(errorData.error || 'Upload fallito');
        }

        const result = await response.json() as { url: string };
        setPhotoUrls(prev => [...prev, result.url]);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Errore durante il caricamento della foto');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove photo
  const handleRemovePhoto = async (url: string) => {
    // Remove from state only (don't delete from storage yet)
    setPhotoUrls(prev => prev.filter(u => u !== url));
  };

  // Cleanup photos on cancel
  const handleCancel = async () => {
    // Find newly uploaded photos that weren't saved (user is canceling)
    const newPhotos = photoUrls.filter(url => !originalPhotoUrls.includes(url));
    
    // Delete new photos from storage
    for (const url of newPhotos) {
      if (url.startsWith('/api/files/')) {
        const key = url.replace('/api/files/', '');
        try {
          await fetch('/api/upload', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key }),
            credentials: 'include'
          });
        } catch (error) {
          console.error('Cleanup error:', error);
        }
      }
    }
    
    navigate(`/assets/${id}`);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Find photos that were removed (existed originally but not in current list)
    const removedPhotos = originalPhotoUrls.filter(url => !photoUrls.includes(url));
    
    // Delete removed photos from storage
    for (const url of removedPhotos) {
      if (url.startsWith('/api/files/')) {
        const key = url.replace('/api/files/', '');
        try {
          await fetch('/api/upload', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ key }),
            credentials: 'include'
          });
        } catch (error) {
          console.error('Delete error:', error);
        }
      }
    }
    
    updateMutation.mutate({
      action: 'update',
      name,
      description: description || null,
      categoryId: categoryId || null,
      brand: brand || null,
      model: model || null,
      serialNumber: serialNumber || null,
      notes: notes || null,
      photoUrls: JSON.stringify(photoUrls)
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Errore: {error?.message || 'Asset non trovato'}</span>
        </div>
        <Link to="/assets" className="mt-4 btn btn-secondary inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Torna agli asset
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={handleCancel}
          className="icon-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-5 h-5 text-primary-500" />
            <span className="text-sm font-medium text-primary-600">Modifica</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{data.asset.name}</h1>
          <p className="text-sm text-gray-500 font-mono">{data.asset.qrCode}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photos Section */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Foto</h2>
          </div>

          {/* Photo Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
            {photoUrls.map((url, index) => (
              <div key={url} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={url}
                  alt={`Foto ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(url)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg shadow-lg hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            
            {/* Upload Button */}
            <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-primary-400 flex flex-col items-center justify-center cursor-pointer transition-colors bg-gray-50 hover:bg-gray-100">
              {isUploading ? (
                <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Carica foto</span>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          </div>

          {/* Camera capture on mobile */}
          <label className="btn btn-secondary w-full flex items-center justify-center gap-2 cursor-pointer">
            <Camera className="w-4 h-4" />
            Scatta foto
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileUpload}
              className="hidden"
              disabled={isUploading}
            />
          </label>
          
          {/* Upload Error Display */}
          {uploadError && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 font-medium">Errore nel caricamento</p>
                <p className="text-red-600 text-sm">{uploadError}</p>
              </div>
              <button
                type="button"
                onClick={() => setUploadError(null)}
                className="ml-auto p-1 text-red-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Basic Info */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl">
              <Info className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Informazioni Base</h2>
          </div>

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Nome dell'asset..."
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Descrizione</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="input"
                rows={2}
                placeholder="Descrizione opzionale..."
              />
            </div>

            <div className="form-group">
              <label className="form-label flex items-center gap-2">
                <Tag className="w-4 h-4 text-gray-400" />
                Categoria
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="input"
              >
                <option value="">Seleziona categoria...</option>
                {categoriesData?.categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Condition Selection */}
            <div className="form-group">
              <label className="form-label">Condizione</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {conditionOptions.map((opt) => {
                  const isSelected = condition === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setCondition(opt.value)}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1.5 ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 scale-[1.02]'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-xl">{opt.emoji}</span>
                      <span className={`text-xs font-medium ${isSelected ? 'text-primary-700' : 'text-gray-600'}`}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
              <Package className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Dettagli Prodotto</h2>
          </div>

          <div className="space-y-4">
            <div className="form-group">
              <label className="form-label">Numero Seriale</label>
              <input
                type="text"
                value={serialNumber}
                onChange={(e) => setSerialNumber(e.target.value)}
                className="input font-mono"
                placeholder="S/N..."
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="form-group">
                <label className="form-label">Marca</label>
                <input
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  className="input"
                  placeholder="Marca/Produttore..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Modello</label>
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  className="input"
                  placeholder="Modello..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Note</h2>
          </div>

          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input"
            rows={3}
            placeholder="Note aggiuntive..."
          />
        </div>

        {updateMutation.error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 text-sm">{updateMutation.error.message}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 sticky bottom-0 bg-gray-50/80 backdrop-blur-sm py-4 -mx-4 px-4 sm:mx-0 sm:px-0 sm:bg-transparent sm:backdrop-blur-none border-t border-gray-200 sm:border-0">
          <button
            type="button"
            onClick={() => navigate(`/assets/${id}`)}
            className="btn btn-secondary flex-1"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={updateMutation.isPending || !name.trim()}
            className="btn btn-primary flex-1"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Package className="w-4 h-4" />
                Salva Modifiche
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
